import { AxiosRequestHeaders } from 'axios';
import RpcClientFactory from '../rpc-client/factory/rpc-client-factory';
import { capability } from '../rpc-client/config/capabilities';
import { DtoTypes } from '../';
import {
    CarActivationFlowDTO,
    CarHistoryDTO,
    CoverableActivationFlowDTO,
    PriceDTO,
    TypeKeyDTO
} from '../types/dto/pc/top/edge/capabilities/activationflow/dto';
import { DeductibleOptionDTO } from '../types/dto/pc/top/edge/capabilities/onlinesales/dto';

export async function getOrCreateJob(jobNumber: string, headers: AxiosRequestHeaders = {}): Promise<string> {
    return await RpcClientFactory.getInstance().rpcCall<string>(
        capability.PC_ACTIVATIONFLOW,
        'getOrCreateJob',
        [jobNumber],
        headers
    );
}

export async function getSessionId(
    jobNumber: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.SessionIdDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.SessionIdDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getSessionId',
        [jobNumber],
        headers
    );
}

export async function updateCarHistory(
    jobNumber: string,
    sessionId: string,
    carDTO: CarActivationFlowDTO,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ACTIVATIONFLOW,
        'updateCarHistory',
        [jobNumber, sessionId, carDTO],
        headers
    );
}

export async function getCarDeductibleOptions(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DeductibleOptionDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<DeductibleOptionDTO[]>(
        capability.PC_ACTIVATIONFLOW,
        'getCarDeductibleOptions',
        [jobNumber, sessionId],
        headers
    );
}

export async function updateCarDeductible(
    jobNumber: string,
    sessionId: string,
    optionID: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ACTIVATIONFLOW,
        'updateCarDeductible',
        [jobNumber, sessionId, optionID],
        headers
    );
}

export async function getCarHistory(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<CarHistoryDTO> {
    return await RpcClientFactory.getInstance().rpcCall<CarHistoryDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getCarHistory',
        [jobNumber, sessionId],
        headers
    );
}

export async function getCarCoverable(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<CoverableActivationFlowDTO> {
    return await RpcClientFactory.getInstance().rpcCall<CoverableActivationFlowDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getCarCoverable',
        [jobNumber, sessionId],
        headers
    );
}

export async function setCarReplacement(
    jobNumber: string,
    sessionId: string,
    sourceCarfixedId: string,
    targetCarfixedId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ACTIVATIONFLOW,
        'setCarReplacement',
        [jobNumber, sessionId, sourceCarfixedId, targetCarfixedId],
        headers
    );
}

export async function getYearlyMileageCode(
    jobNumber: string,
    sessionId: string,
    vehicleID: string,
    headers: AxiosRequestHeaders = {}
): Promise<string> {
    return await RpcClientFactory.getInstance().rpcCall<string>(
        capability.PC_ACTIVATIONFLOW,
        'getYearlyMileageCode',
        [jobNumber, sessionId, vehicleID],
        headers
    );
}

export async function getPriceData(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<PriceDTO> {
    return await RpcClientFactory.getInstance().rpcCall<PriceDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getPriceData',
        [jobNumber, sessionId],
        headers
    );
}

export async function getDrivingExperienceOptions(headers: AxiosRequestHeaders = {}): Promise<TypeKeyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<TypeKeyDTO[]>(
        capability.PC_ACTIVATIONFLOW,
        'getDrivingExperienceOptions',
        [],
        headers
    );
}

export async function getYearlyMilageOptions(headers: AxiosRequestHeaders = {}): Promise<TypeKeyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<TypeKeyDTO[]>(
        capability.PC_ACTIVATIONFLOW,
        'getYearlyMilageOptions',
        [],
        headers
    );
}

export async function getNumberOfClaimsOptions(headers: AxiosRequestHeaders = {}): Promise<TypeKeyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall<TypeKeyDTO[]>(
        capability.PC_ACTIVATIONFLOW,
        'getNumberOfClaimsOptions',
        [],
        headers
    );
}

export async function getAllInsuranceCompanies(
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.insurancecompany.dto.InsuranceCompanyDTO[]> {
    return await RpcClientFactory.getInstance().rpcCall(
        capability.PC_INSURANCE_COMPANY_LOOKUP,
        'getAllInsuranceCompanies',
        [],
        headers
    );
}

export async function getAccountHolderDetails(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.account.AccountDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getAccountHolderDetails',
        [jobNumber, sessionId],
        headers
    );
}

export async function getPaymentInformation(
    jobNumber: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.PaymentInformationDTO> {
    return await RpcClientFactory.getInstance().rpcCall<DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.PaymentInformationDTO>(
        capability.PC_ACTIVATIONFLOW,
        'getPaymentInformation',
        [jobNumber, sessionId],
        headers
    );
}

export async function updatePaymentInformation(
    paymentData: DtoTypes.PC.top.edge.capabilities.onlinesales.dto.payment.UpdatePaymentDTO,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<void> {
    return await RpcClientFactory.getInstance().rpcCall<void>(
        capability.PC_ACTIVATIONFLOW,
        'updatePaymentInformation',
        [paymentData, sessionId],
        headers
    );
}

export async function issue(
    submissionId: string,
    sessionId: string,
    headers: AxiosRequestHeaders = {}
): Promise<boolean> {
    return await RpcClientFactory.getInstance().rpcCall<boolean>(
        capability.PC_ACTIVATIONFLOW,
        'issue',
        [submissionId, sessionId],
        headers
    );
}
